import * as React from "react";
import { ILabelStyles, IProcessedStyleSet, Label, mergeStyleSets, Stack } from "@fluentui/react";
import { LabelInfoTooltip, RequiredTooltip } from "../Tooltip/Tooltip";
import { BaseInputProps } from "./BaseInput";
import { FormContext, FormState } from "./Form";
import {
  baseLabelStyles,
  iconClass,
  labelStyles,
  stackItemStyles,
  stackItemStylesWithToolTip,
  subFieldBorder,
} from "./InputCommon";

const subFieldStyles = { root: { paddingLeft: "5px" } };

const getLabelStyles = ({
  tooltip,
  subField,
}: Pick<FieldWrapperProps, "tooltip" | "subField">): IProcessedStyleSet<ILabelStyles> => (
  mergeStyleSets(
    baseLabelStyles,
    !tooltip ? labelStyles : {},
    subField ? subFieldStyles : {},
  )
);

export interface FieldWrapperProps extends Pick<BaseInputProps, "required" | "tooltip" | "subField"> {
  /**
   * label for FieldWrapper
   */
  label?: string;
  /**
   * Components to be displayed wrapped with FieldWrapper
   */
  children: React.ReactNode;
}

export const FieldWrapper = ({ label, required, subField, tooltip, children }: FieldWrapperProps): JSX.Element => {
  const form: FormState = React.useContext(FormContext);
  if (!Object.keys(form).length) {
    throw new Error("FieldWrapper should be used within form");
  }

  const labelMarkup = (
    <Stack
      horizontal
      styles={tooltip !== undefined ? stackItemStylesWithToolTip : undefined}
    >
      <Stack horizontal style={{ minWidth: "250px" }}>
        {subField && <span style={subFieldBorder}>{ }</span>}
        <Label styles={getLabelStyles({ tooltip, subField })}>
          {label}
          {label && required && <RequiredTooltip />}
          {label && tooltip !== undefined && (
            <LabelInfoTooltip
              tooltip={tooltip}
              styles={!required ? iconClass : undefined}
            />
          )}
        </Label>
      </Stack>
    </Stack>
  );

  const passPropsToReactElement = (element, props):JSX.Element => {
    if (React.isValidElement(element)) {
      return React.cloneElement(element, props);
    }
    return element;
  };

  const passPropsToChildren = (element, props):JSX.Element | JSX.Element[] => {
    if (!Array.isArray(element)) {
      return passPropsToReactElement(element, props);
    }
    return element.map(childElement => passPropsToReactElement(childElement, props));
  };

  const fieldWrapper = (
    <Stack
      horizontal
      styles={stackItemStyles}
    >
      {labelMarkup}
      <Stack>
        {label && passPropsToChildren(children, { label })}
        {!label && children}
      </Stack>
    </Stack>
  );

  return fieldWrapper;
};
